import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  type ReactFlowInstance,
  useEdgesState,
  useNodesState,
} from 'reactflow';

import { useResizeObserver } from '@/shared/hooks/useResizeObserver';
import type { LineageNodeData } from '@/shared/types/lineageNode';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { LineageControls } from '@/shared/ui/lineage/LineageControls';
import { LineageNode } from '@/shared/ui/lineage/LineageNode';
import { LineageNodeDrawer } from '@/shared/ui/lineage/LineageNodeDrawer';
import { NoDataBoundary } from '@/shared/ui/NoDataBoundary';
import { LoadingState } from '@/shared/ui/states/LoadingState';

import type { PlatformLineageResponse, PlatformLineageView } from '@/features/lineage/api';
import { usePlatformLineageData, usePlatformLineageNodeDrawer } from '@/features/lineage/model';

import { ViewSelector } from '@/widgets/lineage/ViewSelector';

import styles from './PlatformLineageViewer.module.css';

import 'reactflow/dist/style.css';

type PlatformLineageViewerProps = {
  lineageData?: PlatformLineageResponse;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  isLoading?: boolean;
  selectedView: PlatformLineageView;
  onViewChange: (view: PlatformLineageView) => void;
};

const LINEAGE_CONTAINER_MIN_HEIGHT = 300;
const LINEAGE_CONTAINER_HEADER_OFFSET = 140;
const LINEAGE_CONTAINER_FULLSCREEN_HEIGHT_OFFSET = 80;

export const PlatformLineageViewer: React.FC<PlatformLineageViewerProps> = ({
  lineageData,
  isFullscreen = false,
  onToggleFullscreen,
  isLoading = false,
  selectedView,
  onViewChange,
}) => {
  const [selectedNodeFqn, setSelectedNodeFqn] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const lineageContainerHeightOffset = useMemo(() => {
    return isFullscreen ? LINEAGE_CONTAINER_FULLSCREEN_HEIGHT_OFFSET : LINEAGE_CONTAINER_HEADER_OFFSET;
  }, [isFullscreen]);

  const selectedEntity = useMemo(() => {
    if (!selectedNodeFqn || !lineageData?.nodes) {
      return null;
    }
    return lineageData.nodes[selectedNodeFqn]?.entity || null;
  }, [selectedNodeFqn, lineageData]);

  const selectedNodeData = useMemo((): LineageNodeData | null => {
    if (!selectedEntity) return null;

    return {
      label: selectedEntity.name,
      displayName: selectedEntity.displayName,
      entityType: selectedEntity.entityType,
      serviceType: selectedEntity.serviceType,
      fullyQualifiedName: selectedEntity.fullyQualifiedName,
      database: undefined,
      schema: undefined,
      service: undefined,
      tier: selectedEntity.tier
        ? {
            tagFQN: selectedEntity.tier.tagFQN,
            displayName: selectedEntity.tier.displayName || selectedEntity.tier.name,
            name: selectedEntity.tier.name,
            labelType: selectedEntity.tier.labelType,
            description: selectedEntity.tier.description,
            source: selectedEntity.tier.source,
            state: selectedEntity.tier.state,
          }
        : undefined,
      owners: selectedEntity.owners?.map((owner) => ({
        deleted: owner.deleted ?? false,
        displayName: owner.displayName,
        name: owner.name,
        id: owner.id,
        type: owner.type,
        fullyQualifiedName: owner.fullyQualifiedName,
      })),
      upstreamCount: 0,
      downstreamCount: 0,
      nodeDepth: 0,
    };
  }, [selectedEntity]);

  const nodeTypes = useMemo(
    () => ({
      lineageNode: (props: { data: LineageNodeData }) => (
        <LineageNode
          {...props}
          selected={selectedNodeFqn === props.data.fullyQualifiedName}
          onSelect={(node) => {
            setSelectedNodeFqn(node.fullyQualifiedName);
            setIsDrawerOpen(true);
          }}
        />
      ),
    }),
    [selectedNodeFqn]
  );

  const { containerRef, containerHeight } = useResizeObserver({
    minHeight: LINEAGE_CONTAINER_MIN_HEIGHT,
    headerOffset: lineageContainerHeightOffset,
  });

  const { initialNodes, initialEdges } = usePlatformLineageData(lineageData);
  const { infoRows, sections } = usePlatformLineageNodeDrawer(selectedEntity);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const [isInitialFitDone, setIsInitialFitDone] = useState(false);

  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
    setIsInitialFitDone(false);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  useEffect(() => {
    if (reactFlowInstance && nodes.length > 0 && !isInitialFitDone) {
      setTimeout(() => {
        reactFlowInstance.fitView({
          padding: 0.2,
          duration: 200,
          minZoom: 0.5,
          maxZoom: 1.0,
        });
        setIsInitialFitDone(true);
      }, 50);
    }
  }, [reactFlowInstance, nodes, isInitialFitDone]);

  const handleToggleFullscreen = useCallback(() => {
    if (onToggleFullscreen) {
      onToggleFullscreen();
    }
  }, [onToggleFullscreen]);

  if (isLoading) {
    return (
      <div className={styles.lineageViewer}>
        <LoadingState message="계보 정보를 불러오는 중..." />
      </div>
    );
  }

  return (
    <div className={styles.lineageViewer} ref={containerRef}>
      <ErrorBoundary
        FallbackComponent={(props) => (
          <ErrorFallback {...props} title="계보 정보를 불러오지 못했습니다" description="다시 시도해 주세요." />
        )}>
        <LineageControls isFullscreen={isFullscreen} onToggleFullscreen={handleToggleFullscreen} />
        <NoDataBoundary data={initialNodes} emptyDescription="계보 정보가 없습니다">
          <div className={styles.flowContainer} style={{ height: `${containerHeight}px` }}>
            <>
              <ReactFlow
                className="touch-flow"
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                nodeTypes={nodeTypes}
                onInit={setReactFlowInstance}
                minZoom={0.05}
                maxZoom={2}
                defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
                autoPanOnNodeDrag={true}
                panOnDrag={true}
                zoomOnScroll={true}
                zoomOnPinch={true}
                zoomOnDoubleClick={false}
                preventScrolling={true}
                proOptions={{ hideAttribution: true }}>
                <Background />
                <Controls position="top-right" />
                <MiniMap nodeColor="#94a3b8" position="bottom-right" />
              </ReactFlow>
              <ViewSelector selectedView={selectedView} onViewChange={onViewChange} />
              {selectedNodeData && (
                <LineageNodeDrawer
                  open={isDrawerOpen}
                  onClose={() => {
                    setIsDrawerOpen(false);
                    setSelectedNodeFqn(null);
                  }}
                  data={selectedNodeData}
                  infoRows={infoRows}
                  sections={sections}
                />
              )}
            </>
          </div>
        </NoDataBoundary>
      </ErrorBoundary>
    </div>
  );
};
